import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import RoutePageRoutes from "./RoutePageRoutes";
import Login from "./components/Authentication/LoginNew";
import { ThemeProvider } from "./hooks/ThemeContext";
import { AuthProvider } from "./hooks/useAuth";
import Modal from "react-modal";
import { RestaurantProvider, useRestaurant } from "./hooks/RestaurantContext";
import { ConfigProvider, useConfig } from "./hooks/ConfigProvider";
import RootPage from "./RootPage";
import Spinner from "./components/Spinner/Spinner";
import LogoPlatform from "./components/LogoPlatform";
import CookieConsent from "./CookieConsent";
import ThemeCustomizerPage from "./components/ThemeCustomizer/ThemeCustomizerPage";
import AuthHandler from "./AuthHandler";
import { DineCentralAuthProvider } from "./hooks/useDineCentralAuth";
import DineCentralLogin from "./components/Authentication/DineCentralLogin";
import { getRemoteEnv, isSingleStore } from "./config/remoteEnv";
import { ToastContainer } from "react-toastify";

// Admin + live-orders route trees load on demand: they carry the heavy
// back-office libraries (exceljs, xlsx, jspdf, heic2any, leaflet — ~3MB raw,
// ~65% of the old bundle) that customers browsing a menu never need.
const RoutePageAdminRoutes = React.lazy(() => import("./RoutePageAdminRoutes"));
const RoutePageLiveOrders = React.lazy(() => import("./RoutePageLiveOrders"));

// Admin surfaces are OFF unless the build opts in with
// REACT_APP_DISABLE_ADMIN=false — the customer-facing deploy (wild-brook)
// doesn't register /admin or /live at all, so their code chunks are never
// even requested. Store management lives in the client center.
export const ADMIN_DISABLED = process.env.REACT_APP_DISABLE_ADMIN !== "false";

const App = () => {
  Modal.setAppElement("#root");
  return (
    <ConfigProvider>
      <AuthProvider>
        <DineCentralAuthProvider>
          <MainApp />
        </DineCentralAuthProvider>
      </AuthProvider>
    </ConfigProvider>
  );
};

const getBasename = () => {
  const currentHost = window.location.hostname;
  const currentPath = window.location.pathname;
  
  // If accessed via dineplatform.com/r/<appId>, extract the basename
  if ((currentHost === 'localhost' || currentHost === 'dineplatform.com' || currentHost === 'www.dineplatform.com' || currentHost === 'demo.dineplatform.com') && currentPath.startsWith('/r/')) {
    const match = currentPath.match(/^(\/r\/[^\/]+)/);
    return match ? match[1] : '';
  }
  
  // Otherwise, no basename (for <appId>.dineplatform.com)
  return '';
};

const MainApp = () => {
  const { config, error, isLoading } = useConfig();
  const [hasConsent, setHasConsent] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(false);

  const basename = getBasename();

  console.log("=== MAINAPP RENDER ===");
  console.log("window.location.href:", window.location.href);
  console.log("window.location.pathname:", window.location.pathname);
  console.log("Basename:", basename);
  console.log("Config type:", config?.properties?.type);
  console.log("isLoading:", isLoading);
  console.log("error:", error);
  console.log("=====================");

  useEffect(() => {
    // Precise check for Instagram in-app browser
    const isInstagramInAppBrowser = () => {
      const userAgent = navigator.userAgent || '';
      
      // Check for specific Instagram WebView indicators
      const instagramRegex = /Instagram/i;
      
      // Ensure we're not in regular Safari
      const isRegularSafari = /^((?!chrome|android).)*safari/i.test(userAgent) && 
                              !/Instagram/i.test(userAgent);
      
      return instagramRegex.test(userAgent) && !isRegularSafari;
    };
    
    // Determine if we're on iOS
    const isIOS = () => {
      const userAgent = navigator.userAgent || '';
      return /iPad|iPhone|iPod/.test(userAgent);
    };
    
    // Only run the redirect if we're specifically in the Instagram in-app browser
    if (isInstagramInAppBrowser()) {
      const targetUrl = window.location.origin + window.location.pathname + window.location.search;
      
      if (isIOS()) {
        // iOS: Use x-safari prefix to open in Safari
        console.log("Instagram in-app browser on iOS detected, redirecting to Safari...");
        window.location.href = `x-safari-${targetUrl}`;
      } else {
        // Android: Use intent URL to open in default browser
        console.log("Instagram in-app browser on Android detected, redirecting to default browser...");
        
        // Intent URL for opening in Android default browser
        const intentUrl = 
          `intent://${targetUrl.replace(/^https?:\/\//, '')}#Intent;` +
          `scheme=https;` +
          `package=com.android.chrome;` +
          `S.browser_fallback_url=${encodeURIComponent(targetUrl)};` +
          `end`;
          
        window.location.href = intentUrl;
        
        // Fallback in case the intent URL doesn't work
        setTimeout(() => {
          window.location.href = targetUrl;
        }, 500);
      }
    } else {
      console.log("Not in Instagram in-app browser, no redirect needed");
    }
    
    // Original code continues
    if (!window.location.pathname.includes('privacyPolicy') && !window.location.pathname.includes('cookiePolicy')) {
      setViewingPolicy(false);
    }
  }, []);

  // Handle configuration loading errors — retries already happened inside
  // loadConfig, so this is a real outage or dead connection. Keep it calm
  // and give the customer a one-tap way back.
  if (error) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "#0b0b0d", color: "#f0f0f0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: "24px", boxSizing: "border-box", textAlign: "center" }}>
        <div>
          <div style={{ fontSize: "40px", marginBottom: "14px" }}>📶</div>
          <h2 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: 700 }}>Couldn't reach the restaurant</h2>
          <p style={{ margin: "0 0 20px", fontSize: "14px", opacity: 0.65, lineHeight: 1.5 }}>
            Check your connection and try again — it usually comes right back.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "12px 28px", borderRadius: "12px", border: "none", background: "#4169E1", color: "#fff", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Show loading screen while the configuration is being loaded
  if (isLoading) {
    if (window.location.href.includes("/admin")) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100vw",
            height: "100vh",
            backgroundColor: "black",
          }}
        >
          <div style={{ height: "200px" }}>
            <LogoPlatform />
            <div style={{ marginTop: "20px", fontSize: "x-small" }}>
              <Spinner />
            </div>
          </div>
        </div>
      );
    } else {
      // Self-contained: RootPage needs RestaurantProvider (mounted below,
      // after loading) — rendering it here crashed the whole app with
      // "useRestaurant must be used within a RestaurantProvider".
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "#0b0b0d" }}>
          <Spinner />
        </div>
      );
    }
  }

  return (
    <BrowserRouter basename={basename}>
      <RestaurantProvider>
        <RestaurantInitializer />
        <ThemeProvider>
          <div style={{ filter: (hasConsent || viewingPolicy) ? 'none' : 'blur(2px)' }}>
            <div className="status-bar-background"></div>
            {/* ONE app-level toast container — toasts fired on pages
                without their own container (rewards, cart page) used to
                queue invisibly until the order page mounted one. */}
            <ToastContainer limit={1} style={{ zIndex: 9999999999 }} />
            <Routes>
              {/* <Route path="/__/auth/handler" element={<AuthHandler />} /> */}
              <Route path="/auth/handler" element={<AuthHandler />} />
              <Route path="/login" element={<DineCentralLogin />} />
              <Route path="/store-login" element={<Login />} />
              <Route path="/theme-customizer" element={<ThemeCustomizerPage />} />
              {!ADMIN_DISABLED && <Route path="/live/*" element={
                <React.Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}><Spinner /></div>}>
                  <RoutePageLiveOrders />
                </React.Suspense>
              } />}
              {!ADMIN_DISABLED && <Route path="/admin/*" element={
                <React.Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}><Spinner /></div>}>
                  <RoutePageAdminRoutes />
                </React.Suspense>
              } />}
              {/* <Route path="/test/*" element={<RoutePageRoutes testMode={true} />} /> */}
              <Route path="*" element={<RoutePageRoutes testMode={window.location.hostname === 'demo.dineplatform.com'} />} />
            </Routes>
          </div>
          {!hasConsent && 
            <CookieConsent 
              onConsentChange={setHasConsent}
              privacyPolicyUrl="/privacyPolicy"
              onPolicyClick={() => setViewingPolicy(true)}
            />
          }
        </ThemeProvider>
      </RestaurantProvider>
    </BrowserRouter>
  );
};

const RestaurantInitializer: React.FC = () => {
  const { setRestaurantId } = useRestaurant();
  const location = useLocation();
  const { config } = useConfig();
  const env = getRemoteEnv();

  useEffect(() => {
        console.log("=== RESTAURANT INITIALIZER ===");
    console.log("location.pathname:", location.pathname);
    console.log("location.search:", location.search);

    const queryParams = new URLSearchParams(location.search);
    let restaurantId = queryParams.get("r") || "";

    // After setting restaurantId, also persist the table param
    const tableParam = queryParams.get("table");
    if (tableParam) {
      sessionStorage.setItem("tableNumber", tableParam);
    }

    console.log("restaurantId from query:", restaurantId);
    // console.log("config.properties.type:", config.properties.type);

    console.log("CONFIG IS: ", config)
    //TODO: Set env.appType in Core Applications DP Console
    console.log("RLOG: ", env.appType, env.restaurantData)
    if (isSingleStore() && env.restaurantData) {
      console.log("RDATA 1")
      // console.log("REMOTEENV RDATA: ", env.restaurantData && Object.keys(env.restaurantData)[0])
      restaurantId = Object.keys(env.restaurantData)[0] //Object.keys(config.restaurants)[0];
      const params = new URLSearchParams(window.location.search);
      params.set("r", restaurantId);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    }
    console.log("Final restaurantId:", restaurantId);

    setRestaurantId(restaurantId);
  }, [location, setRestaurantId, config, env]);

  return null; // This component only initializes the context
};

export default App;